import React, { useEffect, useState } from 'react';
import { 
  Button, 
  Stack, 
  Box, 
  Table, 
  Text, 
  Loader, 
  ActionIcon, 
  Group, 
  Modal, 
  Card, 
  Badge, 
  useMantineTheme,
  TextInput,
  Select,
  Pagination,
  Grid,
  Paper,
  Collapse,
  NumberInput,
  ScrollArea
} from '@mantine/core';
import { IconTrash, IconEdit, IconFilter, IconSearch, IconX, IconDownload, IconSettings } from '@tabler/icons-react';
import { useMediaQuery } from '@mantine/hooks';
import CreatePackageModal from './components/CreatePackageModal';
import PackageFilters from './components/PackageFilters';
// import ColumnSettingsModal, { ColumnConfig } from './components/ColumnSettingsModal';
import ColumnSettingsModal, { ColumnConfig } from './components/ColumnSettingsModal';
import { ApiService } from '../api/api-service';
import { showNotification } from '@mantine/notifications';

interface PackageMeta {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

// Default column configuration
const DEFAULT_COLUMNS: ColumnConfig[] = [
  { key: 'packageId', label: 'Package ID', visible: true, required: true },
  { key: 'courseName', label: 'Course Name', visible: true },
  { key: 'batchNumber', label: 'Batch Number', visible: true },
  { key: 'studentName', label: 'Student Name', visible: true },
  { key: 'studentEmail', label: 'Student Email', visible: false },
  // { key: 'trainerEmail', label: 'Trainer Email', visible: false },
  { key: 'startDate', label: 'Start Date', visible: true },
  { key: 'endDate', label: 'End Date', visible: true },
  { key: 'totalAmount', label: 'Total Amount', visible: true },
  { key: 'receivedAmount', label: 'Received', visible: true },
  { key: 'remainingAmount', label: 'Remaining', visible: true },
  { key: 'packageType', label: 'Package Type', visible: false },
    { key: 'packageGeneralStatus', label: 'Package Status', visible: true }, // ADD THIS LINE

  { key: 'currency', label: 'Currency', visible: false },
  { key: 'actions', label: 'Actions', visible: true, required: true },
];

function Package() {
  const [opened, setOpened] = useState(false);
  const [packages, setPackages] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingPackage, setEditingPackage] = useState<any | null>(null);
  const [deleteModalOpened, setDeleteModalOpened] = useState(false);
  const [packageToDelete, setPackageToDelete] = useState<any | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Column settings state
  const [columnSettingsOpened, setColumnSettingsOpened] = useState(false);
  const [columns, setColumns] = useState<ColumnConfig[]>(() => {
    // Load from localStorage if available
    const saved = localStorage.getItem('packageTableColumns');
    return saved ? JSON.parse(saved) : DEFAULT_COLUMNS;
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [meta, setMeta] = useState<PackageMeta>({
    total: 0,
    page: 1,
    limit: 20,
    totalPages: 0,
    hasNext: false,
    hasPrevious: false
  });

  // Filter state
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    packageId: '',
    batchNumber: '',
    studentName: '',
    trainerEmail: '',
    courseName: '',
    search: '',
      packageGeneralStatus: '',
  });

  const theme = useMantineTheme();
  const isMobile = useMediaQuery('(max-width: 768px)');

  const fetchPackages = async (page = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20'
      });

      Object.entries(filters).forEach(([key, value]) => {
        if (value) {
          params.append(key, value);
        }
      });

      const res = await ApiService.getInstance().get(`/package?${params.toString()}`);
      
      if (res?.data?.data) {
        let results = res.data.data;
        const pkgIdFilter = (filters as any).packageId;
        if (pkgIdFilter) {
          const idStr = String(pkgIdFilter).trim();
          results = results.filter((pkg: any) => String(pkg.packageId) === idStr);
        }

        setPackages(results);
        setMeta(res.data.meta);
      } else {
        setPackages([]);
        setMeta({
          total: 0,
          page: 1,
          limit: 20,
          totalPages: 0,
          hasNext: false,
          hasPrevious: false
        });
      }
    } catch (err) {
      console.error('Failed to fetch packages', err);
      showNotification({
        title: 'Error',
        message: 'Failed to fetch packages',
        color: 'red'
      });
      setPackages([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPackages(currentPage);
  }, [currentPage]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleFilterChange = (key: string, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleApplyFilters = () => {
    setCurrentPage(1);
    fetchPackages(1);
  };

  const handleClearFilters = () => {
    setFilters({
      packageId: '',
      batchNumber: '',
      studentName: '',
      trainerEmail: '',
      courseName: '',
      search: '',
      packageGeneralStatus:''
    });
    setCurrentPage(1);
    setTimeout(() => fetchPackages(1), 0);
  };

  const handleSaveColumns = (newColumns: ColumnConfig[]) => {
    setColumns(newColumns);
    // Save to localStorage
    localStorage.setItem('packageTableColumns', JSON.stringify(newColumns));
    showNotification({
      title: 'Success',
      message: 'Column settings saved successfully',
      color: 'green'
    });
  };

  const handleExportCSV = async () => {
    try {
      const params: Record<string, string> = {};
      Object.entries(filters).forEach(([key, value]) => {
        if (value) {
          params[key] = String(value);
        }
      });

      const response = await ApiService.getInstance().get('/package/export/csv', {
        params,
        responseType: 'blob',
      });

      const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/octet-stream' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `packages_export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);

      showNotification({ title: 'Success', message: 'Packages exported successfully', color: 'green' });
    } catch (err: any) {
      console.error('Export failed:', err);
      showNotification({ title: 'Error', message: err?.message || 'Failed to export packages', color: 'red' });
    }
  };

  const handleEditPackage = async (pkg: any) => {
    try {
      const res = await ApiService.getInstance().get(`/package/${pkg.packageId}`);
      const fullPackage = res?.data;
      
      setEditingPackage(fullPackage);
      setOpened(true);
    } catch (err) {
      console.error('Failed to fetch package details', err);
      showNotification({
        title: 'Error',
        message: 'Failed to fetch package details',
        color: 'red'
      });
    }
  };

  const handleDeleteClick = (pkg: any) => {
    setPackageToDelete(pkg);
    setDeleteModalOpened(true);
  };

  const handleDeleteConfirm = async () => {
    if (!packageToDelete) return;
    
    setDeleting(true);
    try {
      await ApiService.getInstance().delete(`/package/${packageToDelete.packageId}`);
      
      showNotification({
        title: 'Success',
        message: 'Package deleted successfully',
        color: 'green'
      });
      
      await fetchPackages(currentPage);
      
      setDeleteModalOpened(false);
      setPackageToDelete(null);
    } catch (err: any) {
      console.error('Failed to delete package:', err);
      showNotification({
        title: 'Error',
        message: err?.message || 'Failed to delete package',
        color: 'red'
      });
    } finally {
      setDeleting(false);
    }
  };

  const getCurrencySymbol = (currency: string) => {
    const symbols: Record<string, string> = {
      USD: '$',
      INR: '₹',
      CAD: '$',
      GBP: '£',
      NAIRA: '₦',
      EURO: '€',
      AUD: '$',
      AED: 'د.إ',
    };
    return symbols[currency] || '$';
  };

  // Helper to check if a column is visible
  const isColumnVisible = (key: string) => {
    const col = columns.find(c => c.key === key);
    return col ? col.visible : true;
  };

  const renderMobileCard = (pkg: any, index: number) => {
    const price = parseFloat(pkg.price || 0) || 0;
    const received = parseFloat(pkg.receivedAmount || 0) || 0;
    const remaining = parseFloat(pkg.remainingAmount || 0) || (price - received);
    const currencySymbol = getCurrencySymbol(pkg.currency || 'INR');

    const startDate = pkg.startDate
      ? new Date(pkg.startDate).toLocaleDateString()
      : 'N/A';
    
    const endDate = pkg.endDate
      ? new Date(pkg.endDate).toLocaleDateString()
      : 'N/A';

    return (
      <Card key={pkg.packageId ?? index} shadow="sm" padding="lg" radius="md" withBorder style={{ marginBottom: 16 }}>
        <Group justify="space-between" mb="xs">
          <Text fw={600} size="lg">Package #{pkg.packageId ?? 'N/A'}</Text>
          <Group gap="xs">
            <ActionIcon
              variant="light"
              color="blue"
              onClick={() => handleEditPackage(pkg)}
            >
              <IconEdit size={18} />
            </ActionIcon>
            <ActionIcon
              variant="light"
              color="red"
              onClick={() => handleDeleteClick(pkg)}
            >
              <IconTrash size={18} />
            </ActionIcon>
          </Group>
        </Group>

        <Stack gap="xs">
          {isColumnVisible('courseName') && (
            <Group justify="space-between">
              <Text size="sm" c="dimmed">Course:</Text>
              <Text size="sm" fw={500}>{pkg.courseName ?? 'N/A'}</Text>
            </Group>
          )}
  {isColumnVisible('packageGeneralStatus') && (
  <Group justify="space-between">
    <Text size="sm" c="dimmed">Status:</Text>
    <Badge 
      color={
        pkg.packageGeneralStatus === 'Completed' ? 'green' :
        pkg.packageGeneralStatus === 'In-Progress' ? 'blue' :
        pkg.packageGeneralStatus === 'On-Hold' ? 'orange' :
        pkg.packageGeneralStatus === 'Payment Issue' ? 'red' :
        pkg.packageGeneralStatus === 'Discard' ? 'gray' :
        'cyan'
      }
      variant="light"
      size="sm"
    >
      {pkg.packageGeneralStatus ?? 'Not Started'}
    </Badge>
  </Group>
)}
          {isColumnVisible('studentName') && (
            <Group justify="space-between">
              <Text size="sm" c="dimmed">Student:</Text>
              <Text size="sm" fw={500}>{pkg.studentName ?? 'N/A'}</Text>
            </Group>
          )}

          {isColumnVisible('batchNumber') && (
            <Group justify="space-between">
              <Text size="sm" c="dimmed">Batch:</Text>
              <Text size="sm" fw={500}>{pkg.batchNumber ?? 'N/A'}</Text>
            </Group>
          )}

          {(isColumnVisible('startDate') || isColumnVisible('endDate')) && (
            <Group justify="space-between">
              <Text size="sm" c="dimmed">Duration:</Text>
              <Text size="sm" fw={500}>{startDate} - {endDate}</Text>
            </Group>
          )}

          <Box style={{ 
            marginTop: 8, 
            paddingTop: 12, 
            borderTop: `1px solid ${theme.colors.gray[3]}` 
          }}>
            {isColumnVisible('totalAmount') && (
              <Group justify="space-between" mb={8}>
                <Text size="sm" c="dimmed">Total Amount:</Text>
                <Badge color="blue" variant="light">{currencySymbol}{price.toFixed(2)}</Badge>
              </Group>
            )}

            {isColumnVisible('receivedAmount') && (
              <Group justify="space-between" mb={8}>
                <Text size="sm" c="dimmed">Received:</Text>
                <Badge color="green" variant="light">{currencySymbol}{received.toFixed(2)}</Badge>
              </Group>
            )}

            {isColumnVisible('remainingAmount') && (
              <Group justify="space-between">
                <Text size="sm" c="dimmed">Remaining:</Text>
                <Badge color={remaining > 0 ? "orange" : "gray"} variant="light">
                  {currencySymbol}{remaining.toFixed(2)}
                </Badge>
              </Group>
            )}
          </Box>
        </Stack>
      </Card>
    );
  };

  const renderDesktopTable = () => (
    <ScrollArea>
      <Table highlightOnHover striped>
        <thead>
          <tr>
            {isColumnVisible('packageId') && <th>Package ID</th>}
            {isColumnVisible('courseName') && <th>Course Name</th>}
            {isColumnVisible('packageGeneralStatus') && <th>Package Status</th>} {/* ADD THIS LINE */}

            {isColumnVisible('batchNumber') && <th>Batch Number</th>}
            {isColumnVisible('studentName') && <th>Student Name</th>}
            {isColumnVisible('studentEmail') && <th>Student Email</th>}
            {/* {isColumnVisible('trainerEmail') && <th>Trainer Email</th>} */}
            {isColumnVisible('packageType') && <th>Package Type</th>}
            
            {isColumnVisible('startDate') && <th>Start Date</th>}
            {isColumnVisible('endDate') && <th>End Date</th>}
            {isColumnVisible('currency') && <th>Currency</th>}
            {isColumnVisible('totalAmount') && <th>Total Amount</th>}
            {isColumnVisible('receivedAmount') && <th>Received</th>}
            {isColumnVisible('remainingAmount') && <th>Remaining</th>}
            {isColumnVisible('actions') && <th>Actions</th>}
          </tr>
        </thead>
        <tbody>
          {packages.map((pkg, i) => {
            const price = parseFloat(pkg.price || 0) || 0;
            const received = parseFloat(pkg.receivedAmount || 0) || 0;
            const remaining = parseFloat(pkg.remainingAmount || 0) || (price - received);
            const currencySymbol = getCurrencySymbol(pkg.currency || 'INR');

            const startDate = pkg.startDate
              ? new Date(pkg.startDate).toLocaleDateString()
              : 'N/A';
            
            const endDate = pkg.endDate
              ? new Date(pkg.endDate).toLocaleDateString()
              : 'N/A';

            return (
              <tr key={pkg.packageId ?? i}>
                {isColumnVisible('packageId') && (
                  <td>
                    <Button
                      size="xs"
                      onClick={() => handleEditPackage(pkg)}
                    >
                      {pkg.packageId ?? 'N/A'}
                    </Button>
                  </td>
                )}
                {isColumnVisible('courseName') && <td>{pkg.courseName ?? 'N/A'}</td>}
                {isColumnVisible('packageGeneralStatus') && ( // ADD THIS BLOCK
  <td>
    <Badge 
      color={
        pkg.packageGeneralStatus === 'Completed' ? 'green' :
        pkg.packageGeneralStatus === 'In-Progress' ? 'blue' :
        pkg.packageGeneralStatus === 'On-Hold' ? 'orange' :
        pkg.packageGeneralStatus === 'Payment Issue' ? 'red' :
        pkg.packageGeneralStatus === 'Discard' ? 'gray' :
        'cyan'
      }
      variant="light"
    >
      {pkg.packageGeneralStatus ?? 'Not Started'}
    </Badge>
  </td>
)}
                {isColumnVisible('batchNumber') && <td>{pkg.batchNumber ?? 'N/A'}</td>}
                {isColumnVisible('studentName') && <td>{pkg.studentName ?? 'N/A'}</td>}
                {isColumnVisible('studentEmail') && <td>{pkg.studentEmail ?? 'N/A'}</td>}
                {/* {isColumnVisible('trainerEmail') && <td>{pkg.trainerEmail ?? 'N/A'}</td>} */}
                {isColumnVisible('packageType') && <td>{pkg.packageType ?? 'N/A'}</td>}
                {isColumnVisible('startDate') && <td>{startDate}</td>}
                {isColumnVisible('endDate') && <td>{endDate}</td>}
                {isColumnVisible('currency') && <td>{pkg.currency ?? 'INR'}</td>}
                {isColumnVisible('totalAmount') && <td>{currencySymbol}{price.toFixed(2)}</td>}
                {isColumnVisible('receivedAmount') && <td>{currencySymbol}{received.toFixed(2)}</td>}
                {isColumnVisible('remainingAmount') && <td>{currencySymbol}{remaining.toFixed(2)}</td>}
                {isColumnVisible('actions') && (
                  <td>
                    <ActionIcon
                      color="red"
                      variant="subtle"
                      onClick={() => handleDeleteClick(pkg)}
                    >
                      <IconTrash size={18} />
                    </ActionIcon>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </Table>
    </ScrollArea>
  );

  return (
    <div>
      <Stack gap="md">
        {/* Header Section */}
        <Group justify="space-between">
          <Button
            onClick={() => {
              setEditingPackage(null);
              setOpened(true);
            }}
            fullWidth={isMobile}
          >
            Create New Package
          </Button>
          
          <Group>
            <Button
              leftSection={<IconFilter size={18} />}
              variant={showFilters ? "filled" : "light"}
              onClick={() => setShowFilters(!showFilters)}
            >
              Filters
            </Button>
            
            <Button
              leftSection={<IconDownload size={18} />}
              variant="light"
              onClick={handleExportCSV}
            >
              Export CSV
            </Button>

            <ActionIcon
              size="lg"
              variant="light"
              color="gray"
              onClick={() => setColumnSettingsOpened(true)}
              title="Customize Columns"
            >
              <IconSettings size={20} />
            </ActionIcon>
          </Group>
        </Group>

        <PackageFilters
          showFilters={showFilters}
          onToggleFilters={(open) => setShowFilters(open)}
          filters={filters as any}
          onFilterChange={handleFilterChange}
          onApply={handleApplyFilters}
          onClear={handleClearFilters}
        />

        <CreatePackageModal
          opened={opened}
          onClose={() => {
            setOpened(false);
            setEditingPackage(null);
          }}
          onCreated={() => fetchPackages(currentPage)}
          initialData={editingPackage ?? {}}
        />

        {/* Column Settings Modal */}
        <ColumnSettingsModal
          opened={columnSettingsOpened}
          onClose={() => setColumnSettingsOpened(false)}
          columns={columns}
          onSave={handleSaveColumns}
        />

        {/* Delete Confirmation Modal */}
        <Modal
          opened={deleteModalOpened}
          onClose={() => {
            setDeleteModalOpened(false);
            setPackageToDelete(null);
          }}
          title="Delete Package"
          centered
        >
          <Stack>
            <Text>
              Are you sure you want to delete package <strong>{packageToDelete?.packageId}</strong>?
            </Text>

            <Group justify="flex-end" mt="md">
              <Button
                variant="default"
                onClick={() => {
                  setDeleteModalOpened(false);
                  setPackageToDelete(null);
                }}
                disabled={deleting}
              >
                Cancel
              </Button>
              <Button
                color="red"
                onClick={handleDeleteConfirm}
                loading={deleting}
              >
                Delete
              </Button>
            </Group>
          </Stack>
        </Modal>

        {/* Results Info */}
        <Group justify="space-between">
          <Text size="sm" c="dimmed">
            Showing {packages.length > 0 ? ((currentPage - 1) * meta.limit) + 1 : 0} - {Math.min(currentPage * meta.limit, meta.total)} of {meta.total} packages
          </Text>
        </Group>

        {/* Table/Cards */}
        <Box>
          {loading ? (
            <Box style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
              <Loader />
            </Box>
          ) : packages.length === 0 ? (
            <Text c="dimmed" ta="center" py={40}>
              {Object.values(filters).some(v => v) ? 'No packages match your filters.' : 'No packages found.'}
            </Text>
          ) : (
            <>
              {isMobile ? (
                <Box>
                  {packages.map((pkg, i) => renderMobileCard(pkg, i))}
                </Box>
              ) : (
                renderDesktopTable()
              )}
            </>
          )}
        </Box>

        {/* Pagination */}
        {meta.totalPages > 1 && (
          <Group justify="center" mt="xl">
            <Pagination
              total={meta.totalPages}
              value={currentPage}
              onChange={handlePageChange}
              size={isMobile ? 'sm' : 'md'}
            />
          </Group>
        )}
      </Stack>
    </div>
  );
}

export default Package;



